/**
 * feat-300 — "[2] need you ↓" in the page toolbar (design NOTES §3.9; spec §8;
 * AT-15). Toolbar only — no floating pill (coordinator ruling on design Q1).
 *
 * N = open threads on rendered questions in `needs_reply | proposal_ready |
 * failed` (ADR `needsYou`). Hidden at 0, divider included. A click jumps to the
 * next one in DOCUMENT order below the scroll-spy line (wrapping), expands its
 * collapsed sections through feat-298's `revealItem`, opens its panel and
 * focuses the panel heading.
 *
 * When the thread list failed to load: no count, a quiet note + Retry instead.
 */
import React, { RefObject } from 'react';
import { AlertTriangle, ArrowDown } from 'lucide-react';
import { findNavTarget, lessonKey } from '../reviewKeys';
import { TOOLBAR_BTN } from '../ReviewToolbar';
import { useNeedsYou, QuestionPlacement } from './QuestionEditProvider';
import { COUNTER_BADGE } from './classes';
import { QUESTION_EDIT_COPY as COPY } from './voiceEditCopy';

function findItem(root: ParentNode, itemKey: string): HTMLElement | null {
  const nodes = root.querySelectorAll<HTMLElement>('[data-item-key]');
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i].getAttribute('data-item-key') === itemKey) return nodes[i];
  }
  return null;
}

/** Where a needs-you question sits on screen: its card, else (collapsed) its
 *  Drill banner, else its Lesson banner. `null` when none is rendered. */
function topOf(root: ParentNode, p: QuestionPlacement): number | null {
  const el =
    findItem(root, p.itemKey) ??
    findNavTarget(root, p.drillKey) ??
    findNavTarget(root, lessonKey(p.drillKey.replace(/^u/, '').split('-l')[0]));
  return el ? el.getBoundingClientRect().top : null;
}

/** NOTES §3.9 step 2: the first needs-you question whose top is below the
 *  activation line; none → wrap to the first. */
export function pickNext(
  root: ParentNode | null,
  ordered: QuestionPlacement[],
  line: number,
): QuestionPlacement | null {
  if (!ordered.length) return null;
  if (!root) return ordered[0];
  for (const p of ordered) {
    const top = topOf(root, p);
    if (top !== null && top > line) return p;
  }
  return ordered[0];
}

export const NeedsYouCounter: React.FC<{
  rootRef: RefObject<HTMLElement | null>;
  /** The scroll-spy activation line, px from the viewport top. */
  line: number;
  onReveal: (drillKey: string, itemKey: string) => void;
}> = ({ rootRef, line, onReveal }) => {
  const needs = useNeedsYou();
  if (!needs) return null;
  const { ids, ctx } = needs;

  if (ctx.loadFailed && !ctx.loaded) {
    return (
      <>
        <span className="w-px h-5 bg-black/10 mx-1" aria-hidden />
        <span className="inline-flex items-center gap-1.5 text-xs text-inkMuted" data-testid="qe-load-failed">
          <AlertTriangle className="w-3.5 h-3.5" aria-hidden />
          {COPY.threadsLoadFailed}{' '}
          <button
            type="button"
            className="font-semibold underline underline-offset-2 hover:no-underline rounded"
            onClick={ctx.reload}
          >
            {COPY.retry}
          </button>
        </span>
      </>
    );
  }

  const n = ids.length;
  const jump = () => {
    const ordered = ids
      .map((id) => ctx.placementOf(id))
      .filter((p): p is QuestionPlacement => !!p);
    const target = pickNext(rootRef.current, ordered, line);
    if (!target) return;
    onReveal(target.drillKey, target.itemKey);
    ctx.setIntent(target.questionId, 'show');
    ctx.setPanelOpen(target.questionId, true);
    ctx.requestFocus(target.questionId);
  };

  return (
    <>
      {n > 0 && <span className="w-px h-5 bg-black/10 mx-1" aria-hidden />}
      <div aria-live="polite">
        {n > 0 && (
          <button
            type="button"
            className={TOOLBAR_BTN}
            aria-label={COPY.needsYouAria(n)}
            title={COPY.needsYouTitle}
            onClick={jump}
          >
            <span className={COUNTER_BADGE}>{n}</span>
            {COPY.needsYou}
            <ArrowDown className="w-3.5 h-3.5 text-inkMuted" aria-hidden />
          </button>
        )}
      </div>
    </>
  );
};

export default NeedsYouCounter;
